import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Search, Plug } from 'lucide-react';
import axiosInstance from '@/lib/api/axios-instance';
import { useToast } from '@/lib/hooks/use-toast';

interface ProfitCenter {
  id: string;
  code: string;
  name: string;
  longName: string | null;
  controllingArea: string | null;
  segment: string | null;
  hierarchy: string | null;
  responsibleUser: string | null;
  validityStart: string | null;
  validityEnd: string | null;
  isBlocked: boolean;
  isActive: boolean;
  lastSapSyncAt: string | null;
}

export function ProfitCenterTable() {
  const { toast } = useToast();
  const [items, setItems] = useState<ProfitCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/profit-centers', {
        params: { search: search || undefined, limit: 200 },
      });
      setItems(data.data || []);
      setTotal(data.total ?? 0);
    } catch (err: any) {
      toast({
        title: 'Error cargando Profit Centers',
        description: err?.response?.data?.error || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axiosInstance.post('/api/profit-centers/sync');
      toast({
        title: 'Sincronización SAP completada',
        description: `${data.data.successCount} actualizados, ${data.data.failedCount} fallidos, ${data.data.skippedCount} omitidos`,
        variant: 'success',
      });
      await fetchItems();
    } catch (err: any) {
      toast({
        title: 'Error sincronizando',
        description: err?.response?.data?.error || err.message,
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchItems(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código, nombre o descripción…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {total} total · {items.length} mostrados
          </span>
          <Button variant="outline" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar SAP'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Cargando Profit Centers...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Plug className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No hay Profit Centers sincronizados</p>
          <p className="text-xs text-muted-foreground mt-1">
            Activá <code>syncProfitCenters: true</code> en la configuración avanzada de tu
            integración SAP y dale a "Sincronizar SAP".
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Controlling Area</TableHead>
                <TableHead>Segment</TableHead>
                <TableHead>Jerarquía</TableHead>
                <TableHead>Vigencia</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Última sync SAP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((pc) => (
                <TableRow key={pc.id}>
                  <TableCell className="font-mono font-semibold">{pc.code}</TableCell>
                  <TableCell>
                    <div className="font-medium">{pc.name}</div>
                    {pc.longName && pc.longName !== pc.name && (
                      <div className="text-xs text-muted-foreground">{pc.longName}</div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{pc.controllingArea ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{pc.segment ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{pc.hierarchy ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {pc.validityStart ? new Date(pc.validityStart).toLocaleDateString('es') : '—'}
                    {' → '}
                    {pc.validityEnd ? new Date(pc.validityEnd).toLocaleDateString('es') : '—'}
                  </TableCell>
                  <TableCell>
                    {pc.isBlocked ? (
                      <Badge variant="destructive">Bloqueado</Badge>
                    ) : (
                      <Badge variant={pc.isActive ? 'success' : 'default'}>
                        {pc.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {pc.lastSapSyncAt ? new Date(pc.lastSapSyncAt).toLocaleString('es') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
